import { useCallback, useMemo } from 'react';
import {
  ContactDto,
  useInfiniteVendorContacts,
  buildSorter,
  buildQueryFilters,
} from '@sib/api';
import { DEFAULT_PAGE } from '@sib/api/constants';
import { ComboboxTagOptionProps } from '@sib/ui/types';
import { useAutocompleteProps } from '@sib/utils';

export interface UseVendorContactsAutocompleteProps {
  organizationUuid?: string | null;
  search?: string;
  useEmail?: boolean;
}

export const useVendorContactsAutocomplete = ({
  organizationUuid,
  search = '',
  useEmail = false,
}: UseVendorContactsAutocompleteProps) => {
  const params = useInfiniteVendorContacts({
    organizationId: organizationUuid as string,
    options: {
      enabled: !!organizationUuid,
    },
    payload: {
      page: DEFAULT_PAGE,
      size: 5,
      sort: [
        buildSorter({
          field: 'name',
          order: 'asc',
        }),
      ],
      ...(search && {
        filter: buildQueryFilters([
          {
            field: 'name',
            operator: 'contains',
            value: search,
          },
        ]),
      }),
    },
  });

  const optionsMapper = useCallback(
    (contact: ContactDto): ComboboxTagOptionProps => {
      const value = useEmail ? contact?.email || '' : contact.id;

      return {
        label: contact.name ?? '',
        value,
        secondaryValue: useEmail ? contact.id : contact.email,
        color: 'var(--color-secondary)',
      };
    },
    [useEmail]
  );

  const autocompleteResult = useAutocompleteProps({
    params,
    filter: search,
    optionsMapper,
  });

  const totalItems = params.data?.pages?.[0]?.totalItems ?? 0;

  return useMemo(
    () => ({
      ...autocompleteResult,
      totalItems,
    }),
    [autocompleteResult, totalItems]
  );
};
